import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GetFleetStatusHistory } from "../../../store/actions/reports/fleet-status-history";
import Loader from "../../helpers/hoc/loader/Loader";
import NoRecordFound from "../../helpers/hoc/noRecordFound";
import "./css/HistoricDetails.css";

const thStyle = (bg, color) => ({
  padding: '10px 14px', textAlign: 'center', fontWeight: 700, fontSize: 13,
  background: bg, color, borderBottom: '2px solid #424666', whiteSpace: 'nowrap'
})

const tdStyle = (align = 'center') => ({
  padding: '8px 14px', textAlign: align, fontSize: 13, borderBottom: '1px solid #2a2e4a', color: '#fff'
})

const summaryTdStyle = (bg, color) => ({
  ...tdStyle(), fontWeight: 700, fontSize: 14, background: bg, color
})

const cardStyle = (gradient, shadow) => ({
  borderRadius: 12, padding: '16px 22px', minWidth: 160, flex: 1,
  background: gradient, boxShadow: `0 4px 16px ${shadow}`,
})

const CARDS = [
  { key: 'totalDeployable', label: 'Total Deployable', gradient: 'linear-gradient(135deg, #642ca9 0%, #8b3fc8 100%)', shadow: 'rgba(100,44,169,0.45)' },
  { key: 'communicating', label: 'Communicating', gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', shadow: 'rgba(5,150,105,0.45)' },
  { key: 'ignOff12h', label: '> 12h Ignition Off', gradient: 'linear-gradient(135deg, #ca8a04 0%, #eab308 100%)', shadow: 'rgba(202,138,4,0.45)' },
  { key: 'nonCommunicating', label: 'Non-Communicating', gradient: 'linear-gradient(135deg, #dc2626 0%, #ef4444 100%)', shadow: 'rgba(220,38,38,0.45)' },
  { key: 'noApi', label: 'No API', gradient: 'linear-gradient(135deg, #0369a1 0%, #0ea5e9 100%)', shadow: 'rgba(3,105,161,0.45)' },
]

const FleetStatusHistory = () => {
  const dispatch = useDispatch()
  const { snapshots, dataCount } = useSelector(state => state.fleetStatusHistory)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [selectedIdx, setSelectedIdx] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const handleSubmit = useCallback(async () => {
    if (!fromDate || !toDate) return
    setIsLoading(true)
    setIsSubmitted(true)
    setSelectedIdx(null)
    await dispatch(GetFleetStatusHistory({ fromDate, toDate }))
    setIsLoading(false)
  }, [dispatch, fromDate, toDate])

  const selected = selectedIdx !== null ? snapshots[selectedIdx] : null

  return (
    <div style={{ padding: '20px 24px', color: '#fff' }}>
      <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Fleet Status History</h2>

      {/* Date filters */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, color: '#a0a3bd' }}>From Date</label>
        <input
          type="date"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          style={inputStyle}
        />
        <label style={{ fontSize: 13, color: '#a0a3bd' }}>To Date</label>
        <input
          type="date"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          style={inputStyle}
        />
        <button onClick={handleSubmit} disabled={!fromDate || !toDate || isLoading} style={btnStyle}>
          {isLoading ? 'Loading...' : 'Search'}
        </button>
      </div>

      <Loader isLoading={isLoading}>
        {isSubmitted && snapshots.length === 0 && <NoRecordFound />}

        {snapshots.length > 0 && (
          <>
            {/* Snapshot selector — date + time pills */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, color: '#a0a3bd', marginBottom: 8, display: 'block' }}>
                Select Snapshot ({dataCount} snapshots found)
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {snapshots.map((s, i) => (
                  <button
                    key={`${s.date}-${s.time}`}
                    onClick={() => setSelectedIdx(i)}
                    style={{
                      padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                      fontSize: 12, fontWeight: 600, transition: 'all 0.2s',
                      background: selectedIdx === i ? '#7c3aed' : '#2a2e4a',
                      color: selectedIdx === i ? '#fff' : '#a0a3bd',
                    }}
                  >
                    {s.date} &middot; {s.time.slice(0, 5)} IST
                  </button>
                ))}
              </div>
            </div>

            {/* Summary cards — show selected snapshot or latest */}
            {(() => {
              const snap = selected || snapshots[snapshots.length - 1]
              return (
                <>
                  <div style={{ fontSize: 13, color: '#a0a3bd', marginBottom: 8 }}>
                    Showing: <strong style={{ color: '#fff' }}>{snap.date}</strong> at <strong style={{ color: '#fff' }}>{snap.time.slice(0, 5)} IST</strong>
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
                    {CARDS.map(c => (
                      <div key={c.key} style={cardStyle(c.gradient, c.shadow)}>
                        <div style={{ fontSize: 28, fontWeight: 800, color: '#fff' }}>{snap[c.key]}</div>
                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', marginTop: 2 }}>{c.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* OEM breakdown table */}
                  {snap.oemBreakdown && snap.oemBreakdown.length > 0 && (
                    <div style={{
                      overflowX: 'auto', borderRadius: 8,
                      border: '1px solid #424666',
                      background: 'linear-gradient(180deg, #252a4a 0%, #1e2240 100%)',
                      boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
                    }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#252a4a' }}>
                            <th style={{ ...thStyle('#252a4a', '#a0a3bd'), textAlign: 'left', minWidth: 140 }}>OEM Name</th>
                            <th style={thStyle('#5241a0', '#e9d5ff')}>Total Deployable</th>
                            <th style={thStyle('#1a6347', '#6ee7b7')}>Communicating</th>
                            <th style={thStyle('#614a00', '#fde68a')}>&gt; 12h Ignition Off</th>
                            <th style={thStyle('#612525', '#fca5a5')}>Non-Communicating</th>
                            <th style={thStyle('#1a4d78', '#93c5fd')}>No API</th>
                          </tr>
                        </thead>
                        <tbody>
                          {snap.oemBreakdown.map((r, i) => (
                            <tr key={r.oem} style={{ background: i % 2 === 0 ? '#1e2240' : '#252a4a' }}>
                              <td style={{ ...tdStyle('left'), fontWeight: 600 }}>{r.oem}</td>
                              <td style={{ ...tdStyle(), fontWeight: 700 }}>{r.totalDeployable}</td>
                              <td style={tdStyle()}>{r.communicating}</td>
                              <td style={tdStyle()}>{r.ignOff12h}</td>
                              <td style={tdStyle()}>{r.nonCommunicating}</td>
                              <td style={tdStyle()}>{r.noApi}</td>
                            </tr>
                          ))}
                          <tr style={{ background: '#131627' }}>
                            <td style={{ ...tdStyle('left'), fontWeight: 700, fontSize: 14 }}>Total</td>
                            <td style={summaryTdStyle('rgba(109,40,217,0.45)', '#fff')}>{snap.totalDeployable}</td>
                            <td style={summaryTdStyle('rgba(5,150,105,0.40)', '#fff')}>{snap.communicating}</td>
                            <td style={summaryTdStyle('rgba(180,130,0,0.40)', '#fff')}>{snap.ignOff12h}</td>
                            <td style={summaryTdStyle('rgba(220,38,38,0.40)', '#fff')}>{snap.nonCommunicating}</td>
                            <td style={summaryTdStyle('rgba(14,116,144,0.45)', '#fff')}>{snap.noApi}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )
            })()}
          </>
        )}
      </Loader>
    </div>
  )
}

const inputStyle = {
  padding: '6px 12px', borderRadius: 6, border: '1px solid #424666',
  background: '#1e2240', color: '#fff', fontSize: 13, outline: 'none',
}

const btnStyle = {
  padding: '7px 20px', borderRadius: 6, border: 'none', cursor: 'pointer',
  background: '#7c3aed', color: '#fff', fontSize: 13, fontWeight: 600,
}

export default FleetStatusHistory
